/**
 * Portable Text Components for Rich Content Rendering
 * 
 * This module provides custom component mappings for Sanity's Portable Text
 * rich text format, enabling consistent styling and functionality across
 * all CMS-driven content on the WoodAlps website.
 * 
 * Component Categories:
 * - Marks: Inline formatting (bold, italic, custom fonts)
 * - Blocks: Block-level elements (paragraphs, headings)
 * - Types: Custom content types (internal links, external links)
 * 
 * Font Integration:
 * - Neuropol: Brand font for special emphasis
 * - Roboto: Standard content font
 * - Dynamic font switching via CMS
 * 
 * Link Handling:
 * - Internal links: Next.js Link component for SPA navigation
 * - External links: Standard anchors with security attributes
 * - Automatic target and rel attributes for external links
 * 
 * Styling Features:
 * - Tailwind CSS integration
 * - Responsive typography
 * - Consistent spacing and hierarchy
 * - Brand-compliant color schemes
 * 
 * @description Custom Portable Text component mappings for consistent CMS content rendering
 */

import Link from 'next/link';

/**
 * Portable Text component configuration
 * Maps Sanity content types to React components with WoodAlps styling
 */
const portableTextComponents = {
  marks: {
    neuropol: ({children}) => <span className="font-neuropol">{children}</span>,
    roboto: ({children}) => <span className="font-roboto">{children}</span>,
    strong: ({children}) => <strong>{children}</strong>,
    em: ({children}) => <em>{children}</em>,
    fontStyle: ({children, value}) => {
      const fontFamily = value?.font || 'default';
      let fontClass = '';
      
      switch(fontFamily) {
        case 'neuropol':
          fontClass = 'font-neuropol';
          break;
        case 'sans-serif':
          fontClass = 'font-sans';
          break;
        case 'serif':
          fontClass = 'font-serif';
          break;
        case 'monospace':
          fontClass = 'font-mono';
          break;
        case 'cursive':
          fontClass = 'font-cursive';
          break;
        default:
          fontClass = '';
      }
      
      return <span className={fontClass}>{children}</span>;
    },
  },
  block: {
    normal: ({children}) => <p className="mb-4 last:mb-0">{children}</p>,
  },
  types: {
    button: ({value}) => {
      // Check if href is internal (starts with '/') or external
      const isInternalLink = value.href?.startsWith('/');
      
      if (isInternalLink) {
        return (
          <div className="mt-auto pt-4">
            <Link href={value.href}
              className="inline-block px-4 py-2 bg-black text-white text-sm font-bold rounded-md hover:bg-gray-800 transition duration-300 ease-in-out">
              {value.label}
            </Link>
          </div>
        );
      }
      
      return (
        <div className="mt-auto pt-4">
          <a href={value.href} 
             className="inline-block px-4 py-2 bg-black text-white text-sm font-bold rounded-md hover:bg-gray-800 transition duration-300 ease-in-out"
             target="_blank" 
             rel="noopener noreferrer">
            {value.label}
          </a>
        </div>
      );
    },
  }
};

// Helper function to render portable text or fallback to string
export const renderPortableText = (portableText, fallbackText) => {
  if (portableText && 
      Array.isArray(portableText) && 
      portableText.length > 0 && 
      portableText.some(block => 
        block.children && 
        block.children.length > 0 && 
        block.children.some(child => child.text && child.text.trim().length > 0)
      )
  ) {
    return portableText;
  }
  
  // Format the fallback text as a proper Portable Text array
  return [
    {
      _type: 'block',
      _key: 'fallback',
      style: 'normal',
      markDefs: [],
      children: [
        {
          _type: 'span',
          _key: 'fallback-span',
          text: fallbackText,
          marks: []
        }
      ]
    }
  ];
};

export default portableTextComponents; 